import { createSelector } from '@reduxjs/toolkit';

import { sortBy } from '@paragon/utilities';

import { filterJob } from '../filterUtilities';
import { RootState } from '../store';
import { getActiveView } from './getActiveView';

const getFilteredJobs = createSelector(
  (state: RootState) => state.jobs.jobs,
  getActiveView,
  (jobs, view) => jobs.filter((job) => filterJob(job, view.jobFilters)),
);

const getSortedFilteredJobs = createSelector(
  getFilteredJobs,
  (state: RootState) => state.grid.sort,
  (jobs, sort) => {
    switch (sort) {
      case 'createdTimestamp':
        return sortBy(jobs, (job) => {
          const date = job[sort];
          return Date.parse(date);
        });
      case 'name':
        return sortBy(jobs, (job) => job[sort].trim().toLowerCase());
      case 'billingLocation':
        return sortBy(jobs, (job) => job[sort]?.trim().toLowerCase());
      case 'regions':
        return jobs.toSorted((a, b) => compareArrays(a.regions, b.regions, (region) => region.toLowerCase()));
      case 'creator':
        return sortBy(jobs, (job) => job.creator!.lastName + job.creator!.firstName);
      case 'assignedTo':
        return jobs.toSorted((a, b) =>
          compareArrays(a.assignedTo ?? [], b.assignedTo ?? [], (user) =>
            (user.firstName + user.lastName).toLowerCase(),
          ),
        );
      case 'jobPriority':
        const priorityMap = { Low: 0, Normal: 1, High: 2 };
        return sortBy(jobs, (job) => priorityMap[job[sort]]);
      case 'sharedWithOrganizations':
        return jobs.toSorted((a, b) =>
          compareArrays(a.sharedWithOrganizations, b.sharedWithOrganizations, (organization) =>
            organization.toLowerCase(),
          ),
        );
      case 'openedBy':
        return jobs.toSorted((a, b) =>
          compareArrays(a.openedBy ?? [], b.openedBy ?? [], (user) => (user.firstName + user.lastName).toLowerCase()),
        );
      default:
        return sortBy(jobs, (job) => job[sort]);
    }
  },
);

function compareArrays<T>(a: readonly T[], b: readonly T[], selector: (value: T) => string) {
  const maxLength = Math.max(a.length, b.length);

  for (let i = 0; i < maxLength; i++) {
    if (i >= a.length || i >= b.length) {
      if (a.length === b.length) {
        return 0;
      }
      return a.length > b.length ? 1 : -1;
    }

    const aValue = selector(a[i]);
    const bValue = selector(b[i]);
    if (aValue !== bValue) {
      return aValue === bValue ? 0 : aValue > bValue ? 1 : -1;
    }
  }

  return 0;
}

export const getAllResults = createSelector(
  getSortedFilteredJobs,
  (state: RootState) => state.grid.isAscending,
  (jobs, isAscending) => (isAscending ? jobs : jobs.toReversed()),
);
